import { FileAPIEntry, FileAction, useFileAPI } from 'components/common/FileAPI'
import { UrlDTO, VisualClientDTO } from 'models/visuals'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useGalleryVisualType } from './GalleryVisualType.context'
import { useGalleryVisualsMeta } from './GalleryVisualsMeta.context'
import { useGalleryOrder } from './GalleryOrder.context'

import { Nullable } from 'models/helpers'
import { UseAPIReturn } from 'utils/API'
import { useAuth0 } from 'utils/auth'
import { VisualDTOIsClientDTO } from 'utils/typeguards'
import { VisualFileType, VisualStatus } from 'constants/visual'
import constate from 'constate'

export interface GalleryVisualMetadata {
  isPurchased: boolean | undefined
  isFavourited: boolean | undefined
  floorPlannerLink: Nullable<string>
  originalName: string | undefined
  status: VisualStatus | undefined
  visualDTO?: VisualClientDTO
}

const getTypedId = (visualType: string, filename: string) =>
  `${visualType}_${filename.substring(0, filename.lastIndexOf('.')) || filename}`

const loadVisualsUrlResolver = (
  assignmentId: string,
  visualType: VisualFileType,
  fileEntry: FileAPIEntry<GalleryVisualMetadata>,
  api: UseAPIReturn<string>
) => {
  return api.post<UrlDTO>(
    '/assignment/{assignmentId}/visual/{filename}',
    { assignmentId, filename: fileEntry.gcFilename! },
    {},
    true,
    { params: { type: visualType } }
  )
}

export const [GalleryVisualsFileAPIProvider, useGalleryVisualsFileAPI] = constate(
  ({ assignmentId }: { assignmentId: string }) => {

    const { roles } = useAuth0()
    const { visualTypeSelect, webType } = useGalleryVisualType()
    const { purchasedVisualsKeys, allVisualsData, listVisualsQuery } = useGalleryVisualsMeta()
    const { sortFunction } = useGalleryOrder()

    const visualController = useFileAPI<GalleryVisualMetadata>(`GALLERY_VISUALS:${assignmentId}`, {
      purgeOnDestroy: false,
    })

    const currentlyVisibleVisuals = useMemo(
      () => visualController.filesByTag[visualTypeSelect] ?? [],
      [visualController.filesByTag, visualTypeSelect]
    )

    const loadVisuals = useCallback((files: Array<string>, visualType: VisualFileType) => {
      visualController.loadFiles(files, {
        loadUrlResolverFnc: (fileEntry, api) => loadVisualsUrlResolver(assignmentId, visualType, fileEntry, api),
      })
    }, [assignmentId, visualController])

    // Ref to always access the latest loadVisuals without adding it to effect deps
    const loadVisualsRef = useRef(loadVisuals)
    loadVisualsRef.current = loadVisuals

    // Sync API visuals into FileAPI state using addFile (skip policy — never overwrites existing entries)
    useEffect(() => {
      if (!allVisualsData?.visuals) return

      const visuals = allVisualsData.visuals as VisualClientDTO[]
      const apiVisualIds = new Set<string>()

      for (const visual of visuals) {
        const id = getTypedId(visualTypeSelect, visual.name)
        apiVisualIds.add(id)
        const metadata: GalleryVisualMetadata = {
          isFavourited: VisualDTOIsClientDTO(visual) ? visual.favorited : undefined,
          isPurchased: purchasedVisualsKeys.has(visual.name),
          floorPlannerLink: VisualDTOIsClientDTO(visual) ? visual.floorPlanSelfEditUrl : undefined,
          originalName: visual.originalName ?? undefined,
          status: visual.status,
          visualDTO: visual,
        }

        // If the visual was replaced (originalName changed), delete the old entry to force re-fetch
        const existingEntry = visualController.files[id]
        if (existingEntry && existingEntry.metadata?.originalName && existingEntry.metadata.originalName !== visual.originalName) {
          visualController.deleteFiles([id], { localOnly: true })
        }

        // For clients the fetched variant (clean vs watermarked) depends on purchase status — if the status
        // changed after the URL was fetched, the loaded URL is the wrong variant, delete to force re-fetch (ISSUE-2390)
        if (
          roles.isClient &&
          existingEntry?.displayUrl &&
          existingEntry.metadata?.isPurchased !== undefined &&
          existingEntry.metadata.isPurchased !== metadata.isPurchased
        ) {
          visualController.deleteFiles([id], { localOnly: true })
        }

        // addFile uses 'skip' policy by default — won't overwrite entries already being loaded/loaded
        visualController.addFile(id, visualController.createEntry({
          id,
          originalFilename: visual.originalName,
          tag: visualTypeSelect,
          gcFilename: visual.name,
          metadata,
        }))

        // Always update metadata for existing entries (editFile only touches specified fields)
        visualController.editFile(id, { metadata })
      }

      // Remove FileAPI entries that no longer exist in API response (e.g. after delete)
      const staleIds = currentlyVisibleVisuals
        .filter(file => !apiVisualIds.has(file.id))
        .map(file => file.id)

      if (staleIds.length > 0) {
        visualController.deleteFiles(staleIds, { localOnly: true })
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allVisualsData?.visuals, purchasedVisualsKeys, visualTypeSelect, roles.isClient])

    // Auto-load URLs for unloaded visuals
    useEffect(() => {
      const filesToLoad = currentlyVisibleVisuals
        .filter((file) => !file.displayUrl && file.action === FileAction.INIT)

      if (!filesToLoad.length) return

      // Clients get the watermarked webType by default — purchased visuals must be fetched in their clean variant instead (ISSUE-2390)
      const isCleanVariantForced = (file: FileAPIEntry<GalleryVisualMetadata>) => roles.isClient && !!file.metadata.isPurchased

      const cleanTypeIds = filesToLoad.filter((file) => isCleanVariantForced(file)).map((file) => file.id)
      const webTypeIds = filesToLoad.filter((file) => !isCleanVariantForced(file)).map((file) => file.id)

      if (cleanTypeIds.length) loadVisualsRef.current(cleanTypeIds, VisualFileType.POST_WEB)

      if (webTypeIds.length) loadVisualsRef.current(webTypeIds, webType)
    }, [currentlyVisibleVisuals, webType, roles.isClient])

    // Computed values
    const computedVisualValues = useMemo(() => {
      const purchasedVisuals: Set<string> = new Set()
      const unpurchasedVisuals: Set<string> = new Set()
      const favouritedVisuals: Set<string> = new Set()
      const favouritedPurchasedVisuals: Set<string> = new Set()
      const favouritedUnpurchasedVisuals: Set<string> = new Set()

      for (const file of visualController.allFilesArray) {
        const gcFilename = file.gcFilename
        if (!gcFilename) continue

        if (file.metadata.isPurchased) {
          purchasedVisuals.add(gcFilename)
        } else {
          unpurchasedVisuals.add(gcFilename)
        }

        if (file.metadata.isFavourited) {
          favouritedVisuals.add(gcFilename)
          if (file.metadata.isPurchased) favouritedPurchasedVisuals.add(gcFilename)
          else favouritedUnpurchasedVisuals.add(gcFilename)
        }
      }

      return {
        purchasedVisuals,
        unpurchasedVisuals,
        favouritedVisuals,
        favouritedPurchasedVisuals,
        favouritedUnpurchasedVisuals,
      }
    }, [visualController.allFilesArray])

    // Sort visuals
    const sortedVisuals = useMemo(
      () => [...currentlyVisibleVisuals].sort((a, b) =>
        sortFunction(a.gcFilename ?? '', b.gcFilename ?? '')
      ),
      [currentlyVisibleVisuals, sortFunction]
    )

    return {
      ...visualController,
      ...computedVisualValues,
      currentlyVisibleVisuals: sortedVisuals,
      loadVisuals,
      assignmentVisualsQuery: listVisualsQuery,
      assignmentVisualsData: allVisualsData,
      purchasedVisualsExist: computedVisualValues.purchasedVisuals.size > 0,
    }
  }
)
